import { PdpComponentNames } from "@utils/customization";

export interface PdpSectionConfig {
  name: string;
  order: number;
  price_modes: number[];
  enabled?: boolean;
}

export const DEFAULT_PDP_SECTIONS: PdpSectionConfig[] = [
  {
    name: PdpComponentNames.PRICE_DISCLAIMER,
    order: 1,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.FEATURE_BUTTONS, order: 2, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.JEWELRY_CONFIGURATOR,
    order: 3,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.PRODUCT_DETAILS, order: 4, price_modes: [0, 1, 2] },
  { name: PdpComponentNames.PERSONALIZATION, order: 5, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.COMPONENTS_SECTION,
    order: 6,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.COMMENTS, order: 7, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.SUPPLIER_CONTAINER,
    order: 8,
    price_modes: [1],
  },
  { name: PdpComponentNames.SUPPLIER_INFO, order: 9, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.SUPPLIER_COMMENT,
    order: 10,
    price_modes: [1],
  },
];

export const DEFAULT_REVIEW_PAGE_SECTIONS: PdpSectionConfig[] = [
  { name: PdpComponentNames.STONE_CARD, order: 1, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.MATCHING_STONE_CARD,
    order: 2,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.SETTING_CARD, order: 3, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.JEWELRY_CONFIGURATOR,
    order: 4,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.PRODUCT_DETAILS, order: 5, price_modes: [0, 1, 2] },
  { name: PdpComponentNames.PERSONALIZATION, order: 6, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.PRICE_DISCLAIMER,
    order: 7,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.COMMENTS, order: 8, price_modes: [0, 1, 2] },
];

export const DEFAULT_MATCH_PAIR_SECTIONS: PdpSectionConfig[] = [
  { name: PdpComponentNames.MATCH_HEADER, order: 1, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.MATCH_PRICE_DESCRIPTION,
    order: 2,
    price_modes: [0, 1, 2],
  },
  {
    name: PdpComponentNames.MATCH_PRODUCT_DETAILS,
    order: 3,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.COMMENTS, order: 4, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.SUPPLIER_CONTAINER,
    order: 5,
    price_modes: [0, 1, 2],
  },
  { name: PdpComponentNames.SUPPLIER_INFO, order: 6, price_modes: [0, 1, 2] },
  {
    name: PdpComponentNames.SUPPLIER_COMMENT,
    order: 7,
    price_modes: [1],
  },
];

export const getVisibleSections = (
  jsonConfig: PdpSectionConfig[] | undefined,
  priceMode: number,
  defaultConfig: PdpSectionConfig[] = DEFAULT_PDP_SECTIONS
): string[] => {
  const config = jsonConfig ?? defaultConfig;
  return config
    .filter(
      (section) =>
        section.enabled !== false && section.price_modes?.includes(priceMode)
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((section) => section.name);
};

// Backward compatible alias for PDP
export const getVisiblePdpSections = (
  jsonConfig: PdpSectionConfig[] | undefined,
  priceMode: number
): string[] => getVisibleSections(jsonConfig, priceMode, DEFAULT_PDP_SECTIONS);

// For Review Page
export const getVisibleReviewPageSections = (
  jsonConfig: PdpSectionConfig[] | undefined,
  priceMode: number
): string[] =>
  getVisibleSections(jsonConfig, priceMode, DEFAULT_REVIEW_PAGE_SECTIONS);

// For Match Pair / Find Match (uses same PDP JSON config for shared components)
export const getVisibleMatchPairSections = (
  jsonConfig: PdpSectionConfig[] | undefined,
  priceMode: number
): string[] =>
  getVisibleSections(jsonConfig, priceMode, DEFAULT_MATCH_PAIR_SECTIONS);
